import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import displayINRCurrency from "../helpers/displayCurrency";
import { toast } from "react-hot-toast";

const IMAGE_BASE_URL = SummaryApi?.productImage?.url
  ? SummaryApi.productImage.url.replace("/api/uploads", "")
  : "http://localhost:3001";

const EditOrders = () => {
  const { id: encodedId } = useParams();
  const navigate = useNavigate();
  const [liveStatus, setLiveStatus] = useState(null);
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const isSyncing = useRef(false);
  const [shippingDetails, setShippingDetails] = useState({
    weight: "500",
    length: "10",
    breadth: "10",
    height: "10",
    city: "",
    state: "Andhra Pradesh",
    pincode: "",
    // Pickup Warehouse Details
    warehouse_name: "PRIMARY HYD WAREHOUSE",
    pickup_contact_name: "Nagavamsi",
    pickup_address: "Enkiyapadu, Vijayawada, Andhra Pradesh, 500045",
    pickup_city: "Vijayawada",
    pickup_state: "Andhra Pradesh",
    pickup_pincode: "500045",
    pickup_phone: "9959412360",
  });

  const getAllowedNextStatuses = (currentStatus) => {
    if (currentStatus === "Order Placed")
      return ["Order Confirmed", "Cancelled"];
    if (currentStatus === "Order Confirmed")
      return ["Order Shipped", "Cancelled"];
    if (currentStatus === "Order Shipped")
      return ["under Progress", "Delivered"];
    if (currentStatus === "Delivered") return ["Returned"];
    return [];
  };

  const isTerminalStatus = (status) =>
    status === "Returned" || status === "Cancelled";
  const updateDatabaseStatus = async (newStatus) => {
    try {
      setUpdating(true);
      const response = await fetch(SummaryApi.ShippingCreate.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: order.order_id,
          order_status: newStatus,
          shipping_details:
            newStatus === "Order Shipped" ? shippingDetails : null,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setOrder((prev) => ({ ...prev, order_status: newStatus }));
        return true;
      }
      return false;
    } catch (err) {
      console.error("Update failed", err);
      return false;
    } finally {
      setUpdating(false);
    }
  };

const fetchTrackingFromBackend = useCallback(async (awb, currentDbStatus) => {
    if (!awb || awb === "0" || awb === 0) return;

    try {
        setIsTrackingLoading(true);
        
        const response = await fetch(SummaryApi.trackOrderById.url, {
            method: SummaryApi.trackOrderById.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tracking_number: awb })
        });

        const result = await response.json();

        // Check the structure: result.data.status based on your JSON
        if (result.status && result.data) {
            const liveStatus = result.data.status; // e.g., "booked"
            setLiveStatus(liveStatus);

            // AUTO-SYNC LOGIC
            // If Nimbus says "booked" but DB says "Order Shipped", update DB to match Nimbus
            if (liveStatus.toLowerCase() !== currentDbStatus?.toLowerCase()) {
                console.log(`Syncing DB status: ${currentDbStatus} -> ${liveStatus}`);
                
                const updateRes = await fetch(SummaryApi.ShippingCreate.url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        order_id: order.order_id,
                        order_status: liveStatus, // Sync to live status
                        shipping_details: null
                    }),
                });

                const updateResult = await updateRes.json();
                if (updateResult.success) {
                    setOrder(prev => ({ ...prev, order_status: liveStatus }));
                    toast.success(`Status auto-synced to ${liveStatus}`, { icon: '🔄' });
                }
            }
        }
    } catch (err) {
        console.error("Tracking Sync Error:", err);
    } finally {
        setIsTrackingLoading(false);
    }
}, [order?.order_id]);

// Update your useEffect to trigger this
useEffect(() => {
    if (order?.tracking_number && order.tracking_number !== "0") {
        fetchTrackingFromBackend(order.tracking_number);
    }
}, [order?.tracking_number, fetchTrackingFromBackend]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!encodedId) return;
      let orderId = atob(encodedId);
      try {
        setLoading(true);
        const response = await fetch(SummaryApi.OrderDetailsById.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_id: orderId }),
        });
        const data = await response.json();
        const orderData = data.data || data.order;
        setOrder(orderData);
        setSelectedStatus(orderData?.order_status || "");
        if (orderData?.tracking_number && orderData.tracking_number !== "0") {
            fetchTrackingFromBackend(orderData.tracking_number, orderData.order_status);
        }

        // Auto-calculate weight and parse city/pin
        const calculatedWeight = orderData.order_items?.reduce(
          (acc, item) =>
            acc + Number(item.weight || 0) * Number(item.quantity || 0),
          0,
        );
        const addressStr = orderData.order_address || "";
        const pincodeMatch = addressStr.match(/\d{6}/);
        const pincode = pincodeMatch ? pincodeMatch[0] : "";
        const cityParts = addressStr.split(",");
        const city = (cityParts[cityParts.length - 1] || "")
          .replace(pincode, "")
          .replace("-", "")
          .replace("(Urban)", "")
          .trim();


        setShippingDetails((prev) => ({
          ...prev,
          weight: calculatedWeight > 0 ? calculatedWeight.toString() : "500",
          city: city || "",
          pincode: pincode || "",
        }));
      } catch (err) {
        setError("Could not load order");
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetails();
  }, [encodedId]);
 const handleManualUpdate = () => {
    if (selectedStatus === "Order Shipped") setShowModal(true);
    else if (window.confirm(`Update status to ${selectedStatus}?`)) {
        updateDatabaseStatus(selectedStatus).then(success => {
            if(success) toast.success("Status updated manually");
        });
    }
  };
  const handleStatusChange = async () => {
    setUpdating(true);
    setShowModal(false);

    try {
      // THE NEW BACKEND STRATEGY:
      // We send the 'shippingDetails' to your server.
      // Your server will then create the 'nimbuspayload' internally.
      const payload = {
        order_id: order.order_id,
        order_status: selectedStatus,
        shipping_details:
          selectedStatus === "Order Shipped" ? shippingDetails : null,
      };

      const response = await fetch(SummaryApi.ShippingCreate.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        toast.success("Order Updated!");
        setOrder((prev) => ({
          ...prev,
          order_status: selectedStatus,
          tracking_number: result.tracking_number || prev.tracking_number,
        }));
        if (result.tracking_number) {
          fetchTrackingFromBackend(result.tracking_number);
        }
      } else {
        throw new Error(result.message || "Failed to update");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleInitialClick = () => {
    if (selectedStatus === "Order Shipped") setShowModal(true);
    else if (window.confirm(`Update status to ${selectedStatus}?`))
      handleStatusChange();
  };

  if (loading || !order) {
    return (
      <div className="h-screen flex items-center justify-center">
        Loading Order Details...
      </div>
    );
  }
const isTrackingMode = order.tracking_number && order.tracking_number !== "0";
  return (
    <div className="min-h-screen bg-pink-50/20 py-8 px-4">
      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl my-auto animate-in fade-in zoom-in duration-300 overflow-hidden">
            <div className="bg-pink-600 p-5 text-white">
              <h3 className="text-xl font-bold">NimbusPost Shipping Setup</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-4 gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="col-span-4 text-[10px] font-bold text-gray-400 uppercase">
                  Dimensions
                </div>
                <Input
                  label="Weight (gm)"
                  val={shippingDetails.weight}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, weight: v })
                  }
                />
                <Input
                  label="Length (cm)"
                  val={shippingDetails.length}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, length: v })
                  }
                />
                <Input
                  label="Breadth (cm)"
                  val={shippingDetails.breadth}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, breadth: v })
                  }
                />
                <Input
                  label="Height (cm)"
                  val={shippingDetails.height}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, height: v })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-3 p-4 border rounded-xl">
                <div className="col-span-2 text-[10px] font-bold text-gray-400 uppercase">
                  Pickup (Warehouse)
                </div>
                <Input
                  label="Warehouse Name"
                  val={shippingDetails.warehouse_name}
                  set={(v) =>
                    setShippingDetails({
                      ...shippingDetails,
                      warehouse_name: v,
                    })
                  }
                />
                <Input
                  label="Pickup Contact"
                  val={shippingDetails.pickup_contact_name}
                  set={(v) =>
                    setShippingDetails({
                      ...shippingDetails,
                      pickup_contact_name: v,
                    })
                  }
                />
                <div className="col-span-2">
                  <Input
                    label="Pickup Address"
                    val={shippingDetails.pickup_address}
                    set={(v) =>
                      setShippingDetails({
                        ...shippingDetails,
                        pickup_address: v,
                      })
                    }
                  />
                </div>
                <Input
                  label="City"
                  val={shippingDetails.pickup_city}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, pickup_city: v })
                  }
                />
                <Input
                  label="Pincode"
                  val={shippingDetails.pickup_pincode}
                  set={(v) =>
                    setShippingDetails({
                      ...shippingDetails,
                      pickup_pincode: v,
                    })
                  }
                />
                <Input
                  label="Phone"
                  val={shippingDetails.pickup_phone}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, pickup_phone: v })
                  }
                />
                <Input
                  label="State"
                  val={shippingDetails.pickup_state}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, pickup_state: v })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-3 p-4 bg-pink-50 rounded-xl">
                <div className="col-span-2 text-[10px] font-bold text-pink-400 uppercase">
                  Customer Location
                </div>
                <Input
                  label="City"
                  val={shippingDetails.city}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, city: v })
                  }
                />
                <Input
                  label="Pincode"
                  val={shippingDetails.pincode}
                  set={(v) =>
                    setShippingDetails({ ...shippingDetails, pincode: v })
                  }
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 bg-gray-100 font-bold rounded-xl"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusChange}
                  className="flex-1 py-3 bg-pink-600 text-white font-bold rounded-xl"
                >
                  Create Shipment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-8xl mx-auto">
        <h1 className="text-3xl font-extrabold text-pink-900 mb-8">
          Edit Order #{order?.order_code}
        </h1>

        <div className="grid lg:grid-cols-3 gap-7 xl:gap-10">
          <div className="lg:col-span-2 space-y-7">
            <div className="bg-white rounded-2xl shadow-lg border border-pink-100/70 overflow-hidden">
              <div className="bg-gradient-to-r from-pink-50 to-pink-100 px-7 py-6 border-b border-pink-200">
                <h2 className="text-2xl font-bold text-pink-800">
                  Order & Customer Details
                </h2>
              </div>
              <div className="p-7 space-y-6">
                <InfoRow label="Customer" value={order.user_name} />
                <InfoRow label="Mobile" value={order.mobile_number || "—"} />
                <InfoRow
                  label="Address"
                  value={order.order_address || "—"}
                  isMultiLine
                />
                <InfoRow
                  label="Order Date"
                  value={new Date(order.order_date_time).toLocaleString(
                    "en-IN",
                  )}
                />
                <InfoRow label="Payment" value={order.payment_mode || "—"} />
                <InfoRow
                  label="Total Amount"
                  value={displayINRCurrency(order.total_amount)}
                  highlight
                />
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-pink-100 p-8">
              <h2 className="text-xl font-bold text-pink-800 mb-6">
                Order Items
              </h2>
              <div className="space-y-4">
                {order.order_items.map((item) => (
                  <div
                    key={item.order_items_id}
                    className="flex items-center gap-4 border-b pb-4"
                  >
                    <img
                      src={`${IMAGE_BASE_URL}/${item.product_img}`}
                      className="w-16 h-16 object-cover rounded-lg"
                      alt=""
                    />
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800">
                        {item.product_name}
                      </h4>
                      <p className="text-sm text-gray-400">
                        Qty: {item.quantity} | Weight: {item.weight}gm
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-pink-600">
                        {displayINRCurrency(item.subtotal)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

         <div className="space-y-6">
  {/* Status Control Card */}
  <div className="bg-white rounded-3xl shadow-sm border border-pink-100 p-8">
    <h2 className="text-xl font-bold text-pink-800 mb-6">Order Status</h2>
    
    <div className="mb-6">
      <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">DB Status</p>
      <span className="px-4 py-1 bg-pink-100 text-pink-700 rounded-full font-bold text-xs uppercase">
        {order.order_status}
      </span>

      {liveStatus && (
        <div className="mt-4">
          <p className="text-[10px] font-bold text-blue-400 uppercase mb-2">Live Status (NimbusPost)</p>
          <span className="px-4 py-1 bg-blue-600 text-white rounded-full font-bold text-xs uppercase animate-pulse">
            {liveStatus}
          </span>
        </div>
      )}
    </div>

    <div className="mb-4">
      <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Status Action</p>
      {order.tracking_number && order.tracking_number !== "0" ? (
        <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <p className="text-xs text-blue-700 font-medium">
            Live tracking active. Manual status changes are locked to prevent sync errors.
          </p>
          <p className="text-xs font-bold text-blue-600 mt-2 uppercase tracking-tight">
            AWB: {order.tracking_number}
          </p>
        </div>
      ) : (
        <>
          <select
            className="w-full p-3 border-2 border-pink-50 rounded-xl focus:border-pink-500 outline-none mb-4"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">Choose status...</option>
            {getAllowedNextStatuses(order.order_status).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            onClick={handleInitialClick}
            disabled={updating || !selectedStatus}
            className="w-full py-4 bg-pink-600 text-white rounded-2xl font-bold shadow-lg disabled:bg-gray-200"
          >
            {updating ? "Updating..." : "Update Order"}
          </button>
        </>
      )}
    </div>
  </div>

  {/* NEW: Tracking Timeline Section */}
  {order.tracking_number && order.tracking_number !== "0" && (
    <div className="bg-white rounded-3xl shadow-sm border border-pink-100 p-8">
      <h3 className="text-lg font-bold text-gray-800 mb-4">Shipment Journey</h3>
      <div className="space-y-4">
        {isTrackingLoading ? (
          <p className="text-xs text-gray-400 italic">Updating journey...</p>
        ) : (
          <div className="border-l-2 border-blue-100 ml-2 pl-4 space-y-6">
            <div className="relative">
                <div className="absolute -left-[21px] top-1 w-3 h-3 bg-blue-600 rounded-full border-2 border-white"></div>
                <p className="text-xs font-bold text-blue-600 uppercase">{liveStatus}</p>
                <p className="text-[10px] text-gray-400">Latest Update</p>
            </div>
            {/* You can map result.data.history here if you store it in state */}
          </div>
        )}
      </div>
    </div>
  )}
</div>
          {/* Shipment Info Section */}
          <div className="bg-white rounded-2xl shadow-lg border border-pink-100/70 p-7">
            <h3 className="text-xl font-bold text-pink-800 mb-6 flex items-center gap-3">
              Shipment Information
            </h3>
            <div className="space-y-5 text-gray-800">
              <div className="grid grid-cols-[140px,1fr] gap-4">
                <span className="font-semibold text-gray-600">
                  Delivery Type:
                </span>
                <span>
                  {order.is_delivery === 1 ? "Home Delivery" : "Pickup"}
                </span>
              </div>
              <div className="grid grid-cols-[140px,1fr] gap-4">
                <span className="font-semibold text-gray-600">Tracking:</span>
                <span className="text-pink-600 font-bold">
                  {order.tracking_number || "Not available"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, val, set }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">
      {label}
    </label>
    <input
      type="text"
      value={val}
      onChange={(e) => set(e.target.value)}
      className="w-full p-2 border rounded-lg text-sm outline-pink-500"
    />
  </div>
);

const InfoRow = ({ label, value, isMultiLine }) => (
  <div className="flex flex-col">
    <span className="text-[10px] font-bold text-gray-400 uppercase">
      {label}
    </span>
    <span
      className={`font-semibold text-gray-800 ${isMultiLine ? "whitespace-pre-line" : ""}`}
    >
      {value || "—"}
    </span>
  </div>
);

export default EditOrders;
